// PostHog product analytics — central, no-op-safe wrapper.
//
// Every export is a no-op until init() runs with a real key, so the app behaves
// identically in dev/local (no VUE_APP_POSTHOG_KEY) — no console errors, no events.
// Build-time key wiring lives in .github/workflows/deploy.yml (see docs/CICD.md §4).
import posthog from 'posthog-js'

let enabled = false

// Event name constants — keep these in one place to avoid typos drifting across files.
export const EVENTS = {
  LOGGED_IN: 'user_logged_in',
  SIGNED_UP: 'user_signed_up',
  LOGGED_OUT: 'user_logged_out',
  REPORT_GENERATED: 'report_generated',
  WIDGET_CREATED: 'widget_created',
  SCHEDULED_REPORT_CREATED: 'scheduled_report_created',
  BULK_REPORT_CREATED: 'bulk_report_created',
  CHECKOUT_STARTED: 'checkout_started'
}

export function init() {
  const key = process.env.VUE_APP_POSTHOG_KEY
  if (!key || enabled) return // no key (dev/local) → clean no-op

  posthog.init(key, {
    api_host: process.env.VUE_APP_POSTHOG_HOST || 'https://app.posthog.com',
    person_profiles: 'identified_only', // don't create anon profiles for the logged-in app
    capture_pageview: false, // captured manually on route change (SPA)
    autocapture: true,
    capture_exceptions: true, // capture unhandled JS errors/rejections as $exception (DIG-128)
    // Session replay enabled for webinar/signup-funnel analysis. Full-fidelity
    // capture (maskAllInputs: false) per product decision — note PostHog still
    // force-masks password fields, but other inputs and on-page customer data
    // (e.g. report pages) ARE captured. Flip maskAllInputs back to true to redact.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false
    }
  })
  enabled = true
}

// Tie subsequent events to a known user. Safe to call repeatedly (idempotent in posthog).
export function identify(user) {
  if (!enabled || !user || !user.id) return
  posthog.identify(String(user.id), {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    company_type: user.company_type,
    plan: user.subscription ? user.subscription.stripe_status : null,
    parent_id: user.parent_id
  })
}

export function capture(event, properties) {
  if (!enabled) return
  posthog.capture(event, properties || {})
}

// Manual SPA pageview (capture_pageview is off so we control this on route change).
export function capturePageview(to) {
  if (!enabled) return
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    route: to ? to.name : undefined,
    // Which plan the signup link points to (set on /signup/plan/:plan_id; undefined elsewhere).
    plan_id: to && to.params ? to.params.plan_id : undefined
  })
}

// Clear identity on logout so the next user isn't merged into the previous person.
export function reset() {
  if (!enabled) return
  posthog.reset()
}

export default { EVENTS, init, identify, capture, capturePageview, reset }
